import {Injectable, Injector} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {ViewMode} from '../models';
import {
    ArticleDto,
    BasketArticleDto,
    IBasketArticleDto,
    PagedResultDtoOfArticleDto,
    WebShopServiceProxy
} from '@shared/service-proxies/service-proxies';
import {tap} from 'rxjs/operators';
import {WebshopService} from '@app/shared/common/shop-system/services/webshop.service';
import {ProductNavigationService} from '@app/webshop/shared/services/product-navigation.service';

declare const abp: any; // Ensure "abp" global object is available

@Injectable({
    providedIn: 'root',
})
export class CombineViewService {
    private webShopServiceProxy: WebShopServiceProxy;
    private webshopService: WebshopService;
    private productNavigationService: ProductNavigationService;

    private readonly viewModeCacheKey = 'viewMode'; // Key for ABP cache or session storage
    private readonly rowsAmountCacheKey = 'rowsAmount'; // Key for ABP cache or session storage

    // Observable for items which will be displayed
    private _items$: BehaviorSubject<BasketArticleDto[]>;
    // Observable for total amount of items
    private _totalAmount$: BehaviorSubject<number>;
    // Observable for viewMode
    private _viewMode$: BehaviorSubject<ViewMode>;
    // Observable for rowsAmount
    private _rowsAmount$: BehaviorSubject<number>;
    //Current page
    private _currentPage$: BehaviorSubject<number>;

    private _showOnlyInTrunk$: BehaviorSubject<number>;

    //Display rows amount per page
    rowsAmount = 12;

    constructor(injector: Injector) {
        this.webShopServiceProxy = injector.get(WebShopServiceProxy);
        this.webshopService = injector.get(WebshopService);
        this.productNavigationService = injector.get(ProductNavigationService);

        const viewMode = this._getViewMode();
        this._viewMode$ = new BehaviorSubject<ViewMode>(viewMode != null ? viewMode === ViewMode.TableView ? ViewMode.TableView : viewMode : ViewMode.ListView);
        this._rowsAmount$ = new BehaviorSubject<number>(this._getRowsAmount() || this.rowsAmount);

        this._currentPage$ = new BehaviorSubject<number>(0);

        this._totalAmount$ = new BehaviorSubject<number>(0);
        this._items$ = new BehaviorSubject<BasketArticleDto[]>(undefined);

        this._showOnlyInTrunk$ = new BehaviorSubject<number>(0);
    }

    flush() {
        this.resetCurrentPage();
        this.resetTotalAmount();
        this.resetItems();
        this.resetSorting();
    }

    //#region RowsAmount
    // Method to toggle view mode
    get currentRowsAmount(): number {
        return this._rowsAmount$.value;
    }

    // Observable for viewMode
    get rowsAmount$(): Observable<number> {
        return this._rowsAmount$.asObservable();
    }

    changeRowsAmount(rows: number): void {
        if (this.currentRowsAmount === rows) {
            return;
        }

        this.setRowsAmount(rows);
    }

    setRowsAmount(rows: number): void {
        this._rowsAmount$.next(rows);
        this._setRowsAmount(rows);
    }

    //#region Cache

    private _getRowsAmount(): number | null {
        const cachedRowsAmount = abp.utils.getCookieValue(this.rowsAmountCacheKey) || sessionStorage.getItem(this.rowsAmountCacheKey);
        return +cachedRowsAmount || null;
    }

    private _setRowsAmount(rows: number): void {
        abp.utils.setCookieValue(this.rowsAmountCacheKey, rows, null, abp.appPath);
        sessionStorage.setItem(this.rowsAmountCacheKey, rows.toString());
    }

    private _clearRowsAmount(): void {
        abp.utils.setCookieValue(this.rowsAmountCacheKey, '', new Date(0), abp.appPath);
        sessionStorage.removeItem(this.rowsAmountCacheKey);
    }

    //#endregion Cache
    //#endregion RowsAmount

    //#region ViewMode

    get currentMode(): ViewMode {
        return this._viewMode$.value;
    }

    // Observable for viewMode
    get viewMode$(): Observable<ViewMode> {
        return this._viewMode$.asObservable();
    }

    // Set viewMode
    setViewMode(viewMode: ViewMode): void {
        this._viewMode$.next(viewMode);
        this._setViewMode(viewMode);
    }

    // Method to toggle view mode
    changeViewMode(mode: ViewMode): void {
        if (this.currentMode === mode) {
            return;
        }

        this.setViewMode(mode);
    }

    get isDataView(): boolean {
        return [ViewMode.TileView, ViewMode.ListView].includes(this.currentMode);
    }

    get isTableView(): boolean {
        return this.currentMode === ViewMode.TableView;
    }

    get isTileView(): boolean {
        return this.currentMode === ViewMode.TileView;
    }

    get isListView(): boolean {
        return this.currentMode === ViewMode.ListView;
    }

    //#region Cache

    private _getViewMode(): ViewMode | null {
        const cachedMode = abp.utils.getCookieValue(this.viewModeCacheKey) || sessionStorage.getItem(this.viewModeCacheKey);
        if (!cachedMode) {
            return null;
        }

        switch (cachedMode as string) {
            case 'TileView':
                return ViewMode.TileView;
            case 'TableView':
                return ViewMode.TableView;
            case 'ListView':
                return ViewMode.ListView;
        }
    }

    private _setViewMode(mode: ViewMode): void {
        abp.utils.setCookieValue(this.viewModeCacheKey, ViewMode[mode], null, abp.appPath);
        sessionStorage.setItem(this.viewModeCacheKey, ViewMode[mode]);
    }

    private _clearViewMode(): void {
        abp.utils.setCookieValue(this.viewModeCacheKey, '', new Date(0), abp.appPath);
        sessionStorage.removeItem(this.viewModeCacheKey);
    }

    //#endregion Cache
    //#endregion ViewMode

    //#region Current Page
    get currentPage(): number | null {
        return this._currentPage$.value;
    }

    get currentPage$(): Observable<number> {
        return this._currentPage$.asObservable();
    }

    setCurrentPage(currentPage: number): void {
        this._currentPage$.next(currentPage);
        this.productNavigationService.setCurrentPage(currentPage);
    }

    resetCurrentPage() {
        this._currentPage$.next(0);
    }
    //#endregion Current Page

    //#region Products

    //#region Total Amount
    get totalAmount(): number {
        return this._totalAmount$.value;
    }

    get totalAmount$(): Observable<number> {
        return this._totalAmount$.asObservable();
    }

    resetTotalAmount() {
        this._totalAmount$.next(0);
    }
    //#endregion Total Amount

    //#region Items

    get items(): BasketArticleDto[] {
        return this._items$.value;
    }

    // Observable for viewMode
    get items$(): Observable<BasketArticleDto[]> {
        return this._items$.asObservable();
    }

    // Set viewMode
    setItems(result: PagedResultDtoOfArticleDto): void {
        const items = result.items.map(x => this._mapToBasketArticles(x));
        //update items
        this._items$.next(items);
        this.productNavigationService.setItems(items);

        //update total amount
        if (result.totalCount !== this.totalAmount) {
            this._totalAmount$.next(result.totalCount);
            this.productNavigationService.setTotalAmount(result.totalCount);
        }
    }

    resetItems() {
        this._items$.next(undefined);
    }

    resetSorting() {
        this.webshopService.resetSorting();
    }

    globalSearch$(
        filter: string | undefined
        , orderGuideId: string | undefined
        , order: string | undefined
        , skip: number | 0
        , take: number | 12)
        : Observable<PagedResultDtoOfArticleDto> {

        this.webshopService.showLoader();

        return this.webShopServiceProxy.getArticles(
            filter,
            orderGuideId,
            undefined,
            undefined,
            order,
            skip,
            take
        )
            .pipe(
                tap((result: PagedResultDtoOfArticleDto) => {
                    this.setItems(result);
                })
            );
    }

    _mapToBasketArticles(articleDto: ArticleDto): BasketArticleDto {
        const input: IBasketArticleDto = articleDto as BasketArticleDto;

        let article = new BasketArticleDto(input);
        //set default quantity
        this._setArticleQuantity(article);

        return article;
    }

    private _setArticleQuantity(article: BasketArticleDto) {
        article.quantity = 1;
    }
    //#endregion Items

    //#endregion Products

    setShowOnlyInTrunk(value: boolean) {
        this._showOnlyInTrunk$.next(value ? 1 : 0);
    }

    get showOnlyInTrunk(): number {
        return this._showOnlyInTrunk$.value;
    }

    get showOnlyInTrunk$(): Observable<number> {
        return this._showOnlyInTrunk$.asObservable();
    }

}

